import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle, Pencil, Receipt, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { CustomerStatusBadge } from '../../components/customers/CustomerStatusBadge';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatCalendarDate, formatInstant } from '../../lib/calendar-date';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canChangeSalesActivity, salesActivityKindLabel } from '../../lib/sales-activity';
import type { SalesActivityDetail } from '../../types/sales-activity';
import { EXPENSE_STATUS_LABELS } from '../../lib/expenses';

/**
 * /sales/meetings/:meetingId — one meeting: its fields, attendees, the
 * receipts filed against it, the follow-up done/undone toggle, edit and
 * delete.
 *
 * Edit, mark done and delete are offered to the owner or an admin / office
 * staff — the API's own rule (sales-activities.routes.ts); every CRM role
 * reads. Delete is a SOFT delete, and a sales user may delete only their own.
 *
 * Linked receipts are as the API sends them: an office view role sees every
 * one, anyone else only their own — so the panel says which it is showing.
 */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 border-t border-fieldiq-border py-3 first:border-t-0 sm:flex-row sm:justify-between">
      <span className="text-sm text-fieldiq-muted">{label}</span>
      <span className="text-sm text-white sm:max-w-[65%] sm:text-right">{children}</span>
    </div>
  );
}

const OFFICE_VIEW_ROLES = ['admin', 'office_staff', 'dispatcher'];

export default function MeetingDetail() {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { formatCurrency } = useLocale();
  const timeZone = useTenantTimeZone();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const query = useQuery({
    queryKey: ['sales-activity', meetingId],
    queryFn: () => api.salesActivities.get(meetingId!).then((r) => r.data.data as SalesActivityDetail),
    enabled: !!meetingId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const m = failure ? undefined : query.data;
  const canChange = canChangeSalesActivity(user, m);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['sales-activity', meetingId] });
    queryClient.invalidateQueries({ queryKey: ['sales-activities'] });
  };

  const doneMutation = useMutation({
    mutationFn: (done: boolean) => api.salesActivities.update(meetingId!, { follow_up_done: done }),
    onSuccess: invalidate,
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not update the follow-up.')),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.salesActivities.remove(meetingId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-activities'] });
      toast.success('Meeting deleted');
      navigate('/sales/meetings', { replace: true });
    },
    onError: (err) => {
      setConfirmDelete(false);
      toast.error(getApiErrorMessage(err, 'Could not delete this meeting.'));
    },
  });

  const receiptHref = m
    ? `/expenses/new?kind=general&customer=${encodeURIComponent(m.customerId)}&meeting=${encodeURIComponent(m.id)}`
    : '';

  return (
    <div data-testid="meeting-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Sales · Meeting"
        title={m?.customerName ?? 'Meeting'}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          m && (
            <>
              <Link to={receiptHref} className="fi-action fi-action--standard border border-white/25 hover:border-white/60">
                <Receipt className="size-4" aria-hidden="true" />
                Add a receipt
              </Link>
              {canChange && (
                <Link to={`/sales/meetings/${m.id}/edit`} className="fi-action fi-action--standard fi-action--primary">
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit
                </Link>
              )}
            </>
          )
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {failure ? (
            <LoadFailureNotice error={queryFailureError(query)} subject="this meeting" />
          ) : !m ? (
            <div className="h-40 animate-pulse rounded-fi-panel bg-fieldiq-card" aria-busy="true" />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <section className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card px-4 py-2">
                  <Row label="Customer">
                    <span className="inline-flex items-center gap-2">
                      <Link to={`/customers/${m.customerId}`} className="font-semibold underline-offset-2 hover:underline">{m.customerName ?? '—'}</Link>
                      <CustomerStatusBadge status={m.customerStatus} />
                    </span>
                  </Row>
                  <Row label="When">{formatInstant(m.occurredAt, timeZone, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) ?? '—'}</Row>
                  <Row label="Kind">{salesActivityKindLabel(m.kind)}</Row>
                  <Row label="Contact">{m.contactName ?? '—'}</Row>
                  <Row label="Rig">{m.rigName ?? '—'}</Row>
                  <Row label="Location">{m.location ?? '—'}</Row>
                  <Row label="Logged by">{m.ownerName ?? '—'}</Row>
                  <Row label="Opportunity">{m.opportunity ? m.opportunity.title : '—'}</Row>
                  <Row label="Attendees">
                    {m.attendees?.length ? m.attendees.map((a) => a.displayName).join(', ') : '—'}
                  </Row>
                </section>

                <div className="space-y-4">
                  <section className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
                    <h2 className="text-xs font-black uppercase tracking-[0.14em] text-fieldiq-muted">Notes</h2>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-white">{m.notes ?? '—'}</p>
                    <h2 className="mt-4 text-xs font-black uppercase tracking-[0.14em] text-fieldiq-muted">Next step</h2>
                    <p className="mt-2 text-sm text-white">{m.nextStep ?? '—'}</p>
                  </section>

                  <section className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
                    <h2 className="text-xs font-black uppercase tracking-[0.14em] text-fieldiq-muted">Follow-up</h2>
                    {m.followUpDate ? (
                      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm text-white">
                          {formatCalendarDate(m.followUpDate) ?? m.followUpDate}
                          {m.followUpDoneAt && <span className="ml-2 text-fieldiq-muted">· done</span>}
                        </p>
                        {canChange && (
                          <button
                            type="button"
                            disabled={doneMutation.isPending}
                            onClick={() => doneMutation.mutate(!m.followUpDoneAt)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                          >
                            {m.followUpDoneAt ? <Circle size={14} aria-hidden="true" /> : <CheckCircle2 size={14} aria-hidden="true" />}
                            {m.followUpDoneAt ? 'Mark not done' : 'Mark done'}
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="mt-2 text-sm text-fieldiq-muted">None set</p>
                    )}
                  </section>

                  <section className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
                    <h2 className="text-xs font-black uppercase tracking-[0.14em] text-fieldiq-muted">Receipts for this meeting</h2>
                    {m.expenses?.length ? (
                      <ul className="mt-2 divide-y divide-fieldiq-border">
                        {m.expenses.map((e) => (
                          <li key={e.id} className="flex items-center justify-between py-2 text-sm">
                            <span className="text-fieldiq-text">
                              {(EXPENSE_STATUS_LABELS as Record<string, string>)[e.status] ?? '—'}
                            </span>
                            <span className="tabular-nums text-white">{formatCurrency(e.amount)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-2 text-sm text-fieldiq-muted">None filed</p>
                    )}
                    <p className="mt-2 text-xs text-fieldiq-muted">
                      {OFFICE_VIEW_ROLES.includes(user?.role ?? '')
                        ? 'Every receipt filed against this meeting.'
                        : 'Only the receipts you filed are shown.'}
                    </p>
                  </section>
                </div>
              </div>

              {canChange && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm font-semibold text-fieldiq-text hover:border-red-500/50"
                  >
                    <Trash2 size={14} aria-hidden="true" /> Delete meeting
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <ConfirmModal
        isOpen={confirmDelete}
        title="Delete meeting"
        body="This removes the meeting from the log and from every count."
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
